export const TRANSLATION_TIMEOUT_MS = 8000;

// i18next-http-backend has no fetch/body deadline. In a suspended WebView even
// abort may leave its promise pending, so settle the backend callback ourselves
// exactly once. Translations never carry an account credential.
export function translationRequest(options, address, payload, callback) {
  const controller = new AbortController();
  let settled = false;
  const finish = (error, result) => {
    if (settled) return;
    settled = true;
    clearTimeout(timer);
    callback(error, result);
  };
  const timer = setTimeout(() => {
    controller.abort();
    finish(new Error('Translation load timed out'), { status: 0 });
  }, TRANSLATION_TIMEOUT_MS);
  Promise.resolve().then(async () => {
    const requestOptions = typeof options.requestOptions === 'function'
      ? options.requestOptions(payload) : options.requestOptions;
    const customHeaders = typeof options.customHeaders === 'function' ? options.customHeaders() : options.customHeaders;
    const response = await fetch(address, {
      credentials: 'same-origin', cache: 'default', ...requestOptions,
      method: payload ? 'POST' : 'GET',
      headers: { ...customHeaders, ...(payload ? { 'Content-Type': 'application/json' } : {}) },
      ...(payload ? { body: JSON.stringify(payload) } : {}), signal: controller.signal,
    });
    if (settled) return;
    if (!response.ok) { finish(null, { status: response.status }); return; }
    const data = await response.text();
    finish(null, { status: response.status, data });
  }).catch(error => finish(error, { status: 0 }));
}

// i18next 22 marks a failed namespace as -1 and skips it even in reloadResources.
// Reset only failed, absent/empty bundles for this language/fallback before a
// new explicit attempt. Exhausted retryable failures can create an empty bundle
// from the backend's `retry=true` flag. Keep any usable previously loaded copy.
export async function retryFailedTranslations(i18n, language) {
  const connector = i18n.services?.backendConnector;
  if (!connector?.state || !i18n.services?.languageUtils) return;
  const languages = i18n.services.languageUtils.toResolveHierarchy(language);
  const namespaces = typeof i18n.options.ns === 'string' ? [i18n.options.ns] : i18n.options.ns || [];
  const retries = [];
  languages.forEach(code => namespaces.forEach(namespace => {
    const key = `${code}|${namespace}`;
    if (connector.state[key] >= 0 || connector.state[key] === undefined) return;
    const bundle = i18n.getResourceBundle(code, namespace);
    if (bundle && Object.keys(bundle).length) return;
    delete connector.state[key];
    // reload bypasses a failed request's empty placeholder. Removing the bundle
    // would also remove the namespace from i18next's global namespace list.
    retries.push(new Promise((resolve, reject) => i18n.reloadResources([code], [namespace], error => {
      if (error) reject(new Error('Translations unavailable'));
      else resolve();
    })));
  }));
  await Promise.all(retries);
}
